import { Theme, Users } from "@just-in-types";
import * as Quagga from "quagga";
import * as Raven from "raven-js";
import * as React from "react";
import styled from "react-emotion";
import { connect } from "react-redux";
import Button from "../components/Button";
import Column from "../components/Column";
import Container from "../components/Container";
import NextButton from "../components/NextButton";
import ResetButton from "../components/ResetButton";
import Row from "../components/Row";
import Title from "../components/Title";
import ErrorBoundary from "../hoc/withErrorBoundary";
import { navigateToNext, NavigateToNext } from "../store/actions";
import { IStrings } from "../store/reducers/strings";

interface IScannerOwnProps {
  path: string;
}

interface IScannerDispatchProps {
  onNavigateToNext: NavigateToNext;
}

interface IScannerStateProps {
  languageStrings: IStrings;
  theme: Theme.Props;
  user: Users.User;
}

type ScannerProps = IScannerOwnProps &
  IScannerDispatchProps &
  IScannerStateProps;

const ScannerContainer = styled(Container)`
  display: grid;
  grid-template-rows: 20vh 55vh 25vh;
`;
const GridRow = styled("div")`
  display: grid;
  grid-template-columns: 12rem 1fr;
  align-items: center;
  justify-content: center;
  width: 640px;
`;

class Scanner extends React.Component<ScannerProps> {
  private static Height = 480;
  private static Width = 640;
  private static Viewport = styled("div")`
    position: relative;
    width: ${Scanner.Width}px;
    height: ${Scanner.Height}px;
  `;

  public state = { height: Scanner.Height, width: Scanner.Width, code: null };

  private video: null | HTMLVideoElement = null;
  private overlay: null | HTMLCanvasElement = null;

  /* 
    If we don't need to compute something in state before the component mounts,
    we really don't need a constructor.
  */

  public componentDidMount() {
    this.startQuagga();
  }

  public componentWillUnmount() {
    if (Quagga) {
      Quagga.stop();
    }
  }

  public render() {
    const {
      languageStrings: strings,
      theme: { colors }
    } = this.props;
    const { code, height, width } = this.state;
    let ScannerVideo = null;
    if (!code) {
      ScannerVideo = (
        <Row align="center" justify="center">
          <video
            autoPlay={true}
            ref={video => (this.video = video)}
            style={{ height, width }}
          />
        </Row>
      );
    }

    return (
      <ErrorBoundary>
        <ScannerContainer>
          <Row align="center" justify="center">
            <Title color={colors.secondary}>
              {strings.receiptCodeInstructions}
            </Title>
          </Row>
          <Column align="center" justify="center">
            <Scanner.Viewport>
              <video
                autoPlay={true}
                ref={video => (this.video = video)}
                width={width}
                height={height}
                style={{ position: "absolute" }}
              />
              <canvas
                ref={overlay => (this.overlay = overlay)}
                width={width}
                height={height}
                style={{ position: "absolute" }}
              />
            </Scanner.Viewport>
            <GridRow>
              <Title color={colors.primary}>Code:</Title>
              <Title color={code ? colors.success : colors.error}>
                {code ? code : "Not Scanned"}
              </Title>
            </GridRow>
          </Column>
          <Row align="center" justify="center">
            <ResetButton />
            <NextButton
              bg={colors.success}
              disabled={!code}
              name="receiptCode"
              value={code}
            >
              {strings.addName}
            </NextButton>
          </Row>
        </ScannerContainer>
      </ErrorBoundary>
    );
  }

  private getStream = () =>
    navigator.mediaDevices.getUserMedia({ video: true });

  private startQuagga = async () => {
    try {
      const stream = await this.getStream();

      if (this.video) {
        this.video.srcObject = stream;

        Quagga.init(
          {
            debug: true,
            decoder: {
              readers: ["code_128_reader"]
            },
            inputStream: { target: this.video }
          },
          (error: any) => {
            if (error) {
              return Raven.captureException(error);
            }

            Quagga.start();

            Quagga.onProcessed(this.drawBoundingBoxes);

            Quagga.onDetected(this.handleCode);
          }
        );
      } else {
        throw new Error("this.video not mounted.");
      }
    } catch (error) {
      Raven.captureException(error);
    }
  };

  private drawBoundingBoxes = (result: any) => {
    if (this.overlay && result) {
      const drawingCtx = this.overlay.getContext("2d");

      if (result.boxes) {
        drawingCtx.clearRect(0, 0, Scanner.Width, Scanner.Height);

        result.boxes
          .filter(box => box !== result.box)
          .forEach(box => {
            Quagga.ImageDebug.drawPath(box, { x: 0, y: 1 }, drawingCtx, {
              color: "green",
              lineWidth: 2
            });
          });
      }

      if (result.box) {
        Quagga.ImageDebug.drawPath(result.box, { x: 0, y: 1 }, drawingCtx, {
          color: "#00F",
          lineWidth: 2
        });
      }

      if (result.codeResult && result.codeResult.code) {
        Quagga.ImageDebug.drawPath(
          result.line,
          { x: "x", y: "y" },
          drawingCtx,
          { color: "red", lineWidth: 3 }
        );
      }
    }
  };

  private handleCode = (data: any) => {
    const code = data && data.codeResult ? data.codeResult.code : null;

    this.setState({ code });
  };
}

const mapStateToProps = ({ router: { languageStrings }, theme, user }) => ({
  languageStrings,
  theme,
  user
});

const mapDispatchToProps = {
  onNavigateToNext: navigateToNext
};

export default connect<IScannerStateProps, IScannerDispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(Scanner);



// WEBPACK FOOTER //
// ./src/views/Scanner.tsx